import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeHasFillDownSquare,
  computeRowSelectionRangeMaxIndex,
  computeRowSelectionRangeMinIndex,
  computeSelectedColIndexActive,
  computeSelectedColIndexMax,
  computeSelectedColIndexMin,
  RowSelection,
} from 'notebook/tables/store/logic/RowSelectionLogic';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {
  createTableEditableStoreStructuredSelector,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

/**
 * Whether there is an active multi-selection whose per-row state should be derived.
 * When false, every per-row selection value collapses to its empty value.
 */
function hasMultiSelection(tableModel: TableModel): boolean {
  return tableModel.selection.hasMultiSelection && tableModel.state.multiSelIndex != null;
}

/**
 * Creates a selector that computes the per-row selection state ({@link RowSelection})
 * for a single row from the {@link TableModel}.
 *
 * Each property is derived independently from a small pure computation in
 * `RowSelectionLogic`, sharing a couple of inner selectors for the selection box
 * and active cell index.
 */
export function createRowSelectionSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  rowIndex: number
): (store: TableEditableStore) => RowSelection {
  // The (merge-expanded) selection box and active cell index for the current
  // multi-selection, or null when there is none. Nulling both together ensures
  // that "no selection" uniformly yields empty per-row values.
  const selectSelectionBox = (store: TableEditableStore): MultiSelectionBox | null => {
    const tableModel = tableSelector(store);
    return hasMultiSelection(tableModel) ? tableModel.selection.expandedBox : null;
  };
  const selectMultiSelIndex = (store: TableEditableStore): TableIndex | null => {
    const tableModel = tableSelector(store);
    return hasMultiSelection(tableModel) ? tableModel.state.multiSelIndex : null;
  };

  return createTableEditableStoreStructuredSelector(
    {
      selectedColIndexMin: (store) => computeSelectedColIndexMin(rowIndex, selectSelectionBox(store)),
      selectedColIndexMax: (store) => computeSelectedColIndexMax(rowIndex, selectSelectionBox(store)),
      selectedColIndexActive: (store) =>
        computeSelectedColIndexActive(
          rowIndex,
          selectMultiSelIndex(store),
          tableSelector(store).props.computedTable
        ),
      hasFillDownSquare: (store) =>
        computeHasFillDownSquare(rowIndex, {
          selectionBox: selectSelectionBox(store),
          tableHasActiveCell: tableSelector(store).props.multiEditing,
          isTableLocked: tableSelector(store).locking.isTableLocked,
          multiActive: tableSelector(store).state.multiActive,
          isFillDownEnabled: tableSelector(store).fillDown.isEnabled,
        }),
      selectionRangeMinIndex: (store) =>
        computeRowSelectionRangeMinIndex(tableSelector(store).selection.multiAxisDimSelection, rowIndex),
      selectionRangeMaxIndex: (store) =>
        computeRowSelectionRangeMaxIndex(tableSelector(store).selection.multiAxisDimSelection, rowIndex),
    },
    createDebuggableSelector.withTypes<TableEditableStore>()
  );
}
